import Input from "components/form/Input";
import React, {useEffect, useState} from "react";
import CustomButton from "components/CustomButton";
import CustomIcon from "components/CustomIcon";
import {useNumberWithMaxAndMin} from "hooks/useInput";
import NumberSelect from "components/form/NumberSelect";
import {parseFloatSafe} from "helpers/helperFunctions";
import FormLabel from "components/form/FormLabel";
import {FOCUS_ITEM_STYLE} from "../enums";
import ArrowDisplayRadioGroup from "../components/ArrowDisplayRadioGroup";
import { MAX_ASPECT_RATIO_APP, MAX_ASPECT_RATIO_WEB, MIN_ASPECT_RATIO_APP, MIN_ASPECT_RATIO_WEB } from '../constants'

const FocusedPresetsForm = ({
                              preset, setPreset, isEdit, setIsEdit, disabled = false, isFocused = true
                            }) => {

    const aspectRatio = useNumberWithMaxAndMin(preset.ar ?? 1, true, MAX_ASPECT_RATIO_APP, MIN_ASPECT_RATIO_APP);
    const aspectRatioWeb = useNumberWithMaxAndMin(preset.ar_web ?? 1, true, MAX_ASPECT_RATIO_WEB, MIN_ASPECT_RATIO_WEB);

    const [numRows, setNumRows] = useState(preset.rows ?? 1);
    const [numRowsWeb, setNumRowsWeb] = useState(preset.rows_web ?? 1);

    const [heightFactor, setHeightFactor] = useState(preset.hf ?? 1);
    const [heightFactorWeb, setHeightFactorWeb] = useState(preset.hf_web ?? 1);

    const isBannerStyle = preset.style === FOCUS_ITEM_STYLE.banner;
    const isHorizontalStyle = preset.style === FOCUS_ITEM_STYLE.horizontal;
    const isNormalStyle = isFocused && (!preset.style || preset.style === FOCUS_ITEM_STYLE.normal);

    const handleEdit = () => {
      if (isEdit) {
        setPreset({
          ...preset,
          ar: parseFloatSafe(aspectRatio.value),
          ar_web: parseFloatSafe(aspectRatioWeb.value),
          rows: numRows,
          rows_web: numRowsWeb,
          hf: heightFactor,
          hf_web: heightFactorWeb,
        });
      }
      setIsEdit(!isEdit);
    };

    useEffect(() => {
      if (preset.ar) {
        aspectRatio.setValue(preset.ar);
        aspectRatioWeb.setValue(preset.ar_web);
        setNumRows(preset.rows ?? 1);
        setNumRowsWeb(preset.rows_web ?? 1);
        setHeightFactor(preset.hf ?? 1);
        setHeightFactorWeb(preset.hf_web ?? 1);
      }

    }, [preset]);

    const isFieldsNotValid = () => {
      return !parseFloatSafe(aspectRatio.value)
        || !parseFloatSafe(aspectRatioWeb.value)
        || (isHorizontalStyle ? (!heightFactor || !setHeightFactorWeb) : isFocused && !isBannerStyle ? (!numRows || !numRowsWeb) : false);
    };

    return (
      <> 
        <div className="flex space-x-4 w-full mb-2 mt-4">
          <div className="flex-1">
            <FormLabel label="Mobile"/>

            {!isEdit ?
              <div
                className={`border rounded-md bg-secondary-100 grid divide-x p-3 ${isFocused && !isBannerStyle ? "grid-cols-2" : "grid-cols-1"}`}>
                <div className="flex-1">
                  <div className="text-xs">Aspect Ratio</div>
                  <div className="font-semibold">{aspectRatio.value}</div>
                </div>
                {(isFocused && !isBannerStyle) &&
                  <div className="flex-1 pl-3">
                    <div className="text-xs">{isHorizontalStyle ? "Height Factor" : "Items in a Row"}</div>
                    <div className="font-semibold">{isHorizontalStyle ? heightFactor : numRows}</div>
                  </div>
                }
              </div> :
              <div className={`grid gap-2 items-end ${isFocused && !isBannerStyle ? "grid-cols-2" : "grid-cols-1"}`}>
                <Input
                  labelClass="text-xs pl-2 font-light whitespace-nowrap overflow-ellipsis"
                  type="text"
                  placeholder={`Aspect Ratio (min: ${"1/" +MAX_ASPECT_RATIO_APP}, max: ${MAX_ASPECT_RATIO_APP})`}
                  hideError={true}
                  disabled={!isEdit}
                  value={aspectRatio.value}
                  onChange={aspectRatio.onChange}
                />

                {
                  isNormalStyle &&
                  <NumberSelect label="Items in a Row" className="mb-1 whitespace-nowrap overflow-ellipsis"
                                disabled={!isEdit} value={numRows}
                                setValue={setNumRows}/>
                }
                {
                  isHorizontalStyle &&
                  <NumberSelect label="Height Factor" className="mb-1 whitespace-nowrap overflow-ellipsis"
                                disabled={!isEdit} value={heightFactor} options={[0.25, 0.5, 0.75, 1, 2, 3]}
                                setValue={setHeightFactor}/>
                }

              </div>
            }

          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <FormLabel label="Web"/>
              {isHorizontalStyle && <ArrowDisplayRadioGroup preset={preset} setPreset={setPreset} disabled={disabled}/>}
            </div>

            {!isEdit ?
              <div
                className={`border rounded-md bg-secondary-100 grid grid-cols-2 divide-x p-3 ${isFocused && !isBannerStyle ? "grid-cols-2" : "grid-cols-1"}`}>
                <div className="flex-1">
                  <div className="text-xs">Aspect Ratio</div>
                  <div className="font-semibold">{aspectRatioWeb.value}</div>
                </div>

                {(isFocused && !isBannerStyle) &&
                  <div className="flex-1 pl-3">
                    <div className="text-xs">{isHorizontalStyle ? "Height Factor" : "Items in a Row"}</div>
                    <div className="font-semibold">{isHorizontalStyle ? heightFactorWeb : numRowsWeb}</div>
                  </div>
                }
              </div>
              :
              <div className={`grid gap-2 items-end ${isFocused && !isBannerStyle ? "grid-cols-2" : "grid-cols-1"}`}>
                <Input
                  labelClass="text-xs pl-2 font-light whitespace-nowrap overflow-ellipsis"
                  type="text"
                  placeholder={`Aspect Ratio (min: ${"1/"+MAX_ASPECT_RATIO_WEB}, max: ${MAX_ASPECT_RATIO_WEB})`}
                  hideError={true}
                  disabled={!isEdit}
                  value={aspectRatioWeb.value}
                  onChange={aspectRatioWeb.onChange}
                />
                {
                  isNormalStyle &&
                  <NumberSelect label="Items in a Row" className="mb-1 whitespace-nowrap overflow-ellipsis"
                                disabled={!isEdit} value={numRowsWeb} max={8}
                                setValue={setNumRowsWeb}/>
                }
                {
                  isHorizontalStyle &&
                  <NumberSelect label="Height Factor" className="mb-1 whitespace-nowrap overflow-ellipsis"
                                disabled={!isEdit} value={heightFactorWeb} max={3} options={[0.25, 0.5, 0.75, 1, 2, 3]}
                                setValue={setHeightFactorWeb}/>
                }
              </div>
            }
          </div>
        </div>
        {!disabled && <div className="space-x-2 flex justify-end">
          <CustomButton
            disabled={isEdit && isFieldsNotValid()}
            onClick={handleEdit}>
            <div>{isEdit ? "Save Changes" : "Change Ratios"}</div>
          </CustomButton>

        </div>
        }
        {/* <div className="flex sm:space-x-2 flex-col sm:flex-row space-y-2 sm:space-y-0 items-end px-2">
     

      <div className="flex space-x-2 flex-1 w-full">
        <Input
          labelClass="text-xs pl-2  whitespace-nowrap overflow-ellipsis"
          type="text"
          placeholder="Aspect Ratio(mobile)"
          hideError={true}
          disabled={!isEdit}
          value={aspectRatio.value}
          onChange={aspectRatio.onChange}
        />
        <Input
          labelClass="text-xs pl-2 whitespace-nowrap overflow-ellipsis"
          type="text"
          placeholder="Aspect Ratio(web)"
          hideError={true}
          disabled={!isEdit}
          value={aspectRatioWeb.value}
          onChange={aspectRatioWeb.onChange}
        />
      </div>
      {
        isFocused ?
          <div className="flex space-x-2 flex-1 w-full">
            <NumberSelect label="Items in a row(mobile):" className="flex-1 mb-1 whitespace-nowrap overflow-ellipsis"
                          disabled={!isEdit} value={numRows} setValue={setNumRows}/>
            <NumberSelect label="Items in a row(web):" className="flex-1 whitespace-nowrap overflow-ellipsis"
                          disabled={!isEdit} value={numRowsWeb} max={8}
                          setValue={setNumRowsWeb}/>
          </div> : <></>
      }

      {!disabled && <div className="pb-1.5 w-full sm:w-auto">
        <CustomButton
          disabled={isFieldsValid()}
          isIconButton={true} className="w-full sm:w-auto space-x-2 flex justify-center items-center"
          onClick={handleEdit}>
          <CustomIcon icon={isEdit ? "done_all" : "edit"}/>
          <div className="sm:hidden">{isEdit ? "Save" : "Edit"}</div>
        </CustomButton>

      </div>
      }
    </div> */}
      </>

    );
  }
;

export default FocusedPresetsForm;
